import { useCallback } from 'react';
import { useRouter, NextRouter } from 'next/router';
import { A, D, G, S } from '@mobily/ts-belt';
export type PaginationParams = {
  page: number;
};
export type UsePagainationParamReturn = {
  paginationParams: PaginationParams;
  /**
   * Use `getPageLinkParams` to generate the `href.query` prop of a Next `Link` component,
   * rendered in the `PaginationLinks` component.
   */
  getPageLinkParams: (page: number | undefined) => ReturnType<typeof removeEmptyQueryParams>;
};
const getPaginationParams = (query: NextRouter['query']) => {
  const {
    page
  } = query;
  const pageParams: PaginationParams = {
    page: parsePageQueryParam(page)
  };
  return pageParams;
};

/**
 * Parses the `page` string query param into a number.
 * If page is not a valid number, returns `0` by default.
 */
const parsePageQueryParam = (page: string | string[] | undefined): number => {
  let validPage = 0;
  if (G.isUndefined(page)) {
    validPage = 1;
  }
  if (G.isString(page)) {
    const parsedPage = parseInt(page, 10);

    // Guard against `parseInt` returning `NaN` when page is an invalid number string, e.g., "foo", "-3", etc.
    if (G.isNumber(parsedPage) && parsedPage > 0) {
      validPage = parsedPage;
    }
  }
  return validPage;
};

/**
 * Merges the new `page` with the existing `query` params.
 * Removes `page` from the href query params when page is 1, or lower.
 */
const getValidPageHrefQuery = (page: number | undefined, query: NextRouter['query']) => {
  let newPageQueryParam: string | undefined;
  if (G.isNumber(page) && page > 1) {
    newPageQueryParam = page.toString();
  }
  return removeEmptyQueryParams({
    ...query,
    page: newPageQueryParam
  });
};
const removeEmptyQueryParams = (queryParams: NextRouter['query']) => {
  return D.filter(queryParams, value => {
    const isNotEmptyString = G.isString(value) && S.isNotEmpty(value);
    const isNotEmptyArray = G.isArray(value) && A.isNotEmpty(value);
    return isNotEmptyString || isNotEmptyArray;
  });
};
export const usePaginationParams = (): UsePagainationParamReturn => {
  const {
    query
  } = useRouter();
  const getPageLinkParams = useCallback<UsePagainationParamReturn['getPageLinkParams']>(page => getValidPageHrefQuery(page, query), [query]);
  return {
    paginationParams: getPaginationParams(query),
    getPageLinkParams
  };
};